import { useMemo, useState } from 'react';
import { Select } from 'antd';
import {
  createCellErrorStyle,
  ErrorCellRenderer,
  HeaderGrid,
} from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useOnlineCoreGridColumns';

// ---------------------------------------------------------------------------
// UPC Select cell editor
// ---------------------------------------------------------------------------

/**
 * Inline select editor for the Unit UPC-12 column in beverage/shipper grids.
 *
 * Each option shows "UPC — Product Name" so users can identify the right product
 * without switching back to the Core Data tab. Per-row exclusion is handled via
 * `cellEditorParams` (function form), so each row only sees UPCs not already
 * claimed by other rows. The row's own current value is always kept selectable.
 */
export const UpcSelectCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? null);

  // AG Grid injects cellEditorParams fields directly onto props
  const availableOptions = props.availableOptions ?? [];

  const handleChange = (selected) => {
    setValue(selected);
    props.onValueChange(selected);
    setTimeout(() => props.stopEditing(), 0);
  };

  return (
    <Select
      autoFocus
      showSearch
      value={value}
      onChange={handleChange}
      style={{ width: '240px' }}
      placeholder='Select UPC'
      // Search across both UPC and product name
      filterOption={(input, option) =>
        String(option?.label ?? '')
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      getPopupContainer={(trigger) => trigger.parentElement}
    >
      {availableOptions.map(({ upc, label }) => (
        <Select.Option key={upc} value={upc} label={label}>
          {label}
        </Select.Option>
      ))}
    </Select>
  );
};
UpcSelectCellEditor.displayName = 'UpcSelectCellEditor';

// ---------------------------------------------------------------------------
// UPC column factory
// ---------------------------------------------------------------------------

/**
 * Creates the Unit UPC-12 column definition for beverage/shipper grids.
 *
 * `cellEditorParams` is a function evaluated per-row at edit time, producing
 * an `availableOptions` list that excludes UPCs already selected by other rows.
 *
 * @param {{ upc: string, label: string }[]} unitUpcOptions
 * @param {Map<string, string>} upcByUniqueId
 * @returns {import('ag-grid-community').ColDef}
 */
const createUpcColumn = (unitUpcOptions, upcByUniqueId) => ({
  field: 'unitUpc',
  headerComponent: HeaderGrid,
  headerComponentParams: { label: 'Unit UPC12', required: true },
  width: 260,
  editable: true,
  singleClickEdit: true,
  resizable: true,
  cellEditor: UpcSelectCellEditor,
  cellEditorPopup: true,
  cellRenderer: ErrorCellRenderer,
  cellStyle: createCellErrorStyle('unitUpc'),
  cellEditorParams: (params) => {
    const currentRowUniqueId = params.data?.uniqueId;
    const currentRowUpc = params.data?.unitUpc;

    const claimedByOthers = new Set();
    upcByUniqueId.forEach((upc, uniqueId) => {
      if (uniqueId !== currentRowUniqueId && upc) {
        claimedByOthers.add(upc);
      }
    });

    const availableOptions = unitUpcOptions.filter(
      ({ upc }) => !claimedByOthers.has(upc) || upc === currentRowUpc
    );

    return { availableOptions };
  },
});

// ---------------------------------------------------------------------------
// Hidden columns for quick filter
// ---------------------------------------------------------------------------

/**
 * Hidden columns that are invisible to the user but included in AG Grid's
 * quick filter scan. This allows searching by product name or brand name
 * in the beverage/shipper tabs even though those fields aren't displayed.
 *
 * AG Grid skips columns where `suppressColumnsToolPanel` isn't set and
 * the column is hidden — but quick filter still scans hidden columns
 * as long as they're in columnDefs.
 */
const HIDDEN_FILTER_COLUMNS = [
  { field: 'unitUpc', hide: true },
  { field: 'vendorItemNumber', hide: true },
  { field: 'brandName', hide: true },
  { field: 'productName', hide: true },
  { field: 'productDescription', hide: true },
  { field: 'ecommerceDescription', hide: true },
  { field: 'size', hide: true },
  { field: 'sizeUom', hide: true },
  { field: 'innerPackUpc', hide: true },
  { field: 'innerPackGtin', hide: true },
  { field: 'innerPackPackSize', hide: true },
  { field: 'caseGtin', hide: true },
  { field: 'casePackSize', hide: true },
  { field: 'caseInnerPackCount', hide: true },
];

// ---------------------------------------------------------------------------
// Static column definitions
// ---------------------------------------------------------------------------

const BEER_STATIC_COLUMNS = [
  {
    field: 'beerRegion',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Beer Region' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'beerCategory',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Beer Category' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'ibus',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'IBUs' },
    width: 110,
    editable: true,
    resizable: true,
  },
  {
    field: 'beerStyle',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Beer Style' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'beerTaste',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Beer Taste' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'beerBody',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Beer Body' },
    width: 140,
    editable: true,
    resizable: true,
  },
  {
    field: 'beerFinalGravity',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Beer Final Gravity' },
    width: 170,
    editable: true,
    resizable: true,
    flex: 1,
  },
];

const WINE_STATIC_COLUMNS = [
  {
    field: 'wineCategory',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Category' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineVarietal',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Varietal' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineSweetness',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Sweetness' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineOrigin',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Origin' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineRegion',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Region' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineCorkType',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Cork Type' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'winePairsWellWith',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Pairs Well With' },
    width: 200,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineComplements',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Complements' },
    width: 200,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineVintage',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Vintage' },
    width: 140,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineTaste',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Taste' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineBody',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Body' },
    width: 140,
    editable: true,
    resizable: true,
  },
  {
    field: 'wineCharacter',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Wine Character' },
    minWidth: 200,
    editable: true,
    resizable: true,
    flex: 1,
  },
];

const SPIRITS_STATIC_COLUMNS = [
  {
    field: 'spiritsFlavor',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Spirits Flavor' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'spiritsCategory',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Spirits Category' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'spiritsSubCategory',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Spirits Sub Category' },
    width: 190,
    editable: true,
    resizable: true,
  },
  {
    field: 'spiritsStyle',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Spirits Style' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'spiritsRegion',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Spirits Region' },
    width: 160,
    editable: true,
    resizable: true,
  },
  {
    field: 'spiritsTaste',
    headerComponent: HeaderGrid,
    headerComponentParams: { label: 'Spirits Taste' },
    width: 160,
    editable: true,
    resizable: true,
    flex: 1,
  },
];

const STATIC_COLUMN_MAP = {
  beer: BEER_STATIC_COLUMNS,
  wine: WINE_STATIC_COLUMNS,
  spirits: SPIRITS_STATIC_COLUMNS,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns column definitions for beverage grids.
 *
 * Includes hidden `productName` and `brandName` columns so AG Grid's quick
 * filter can match on those fields even though they aren't rendered visibly.
 * This keeps cross-tab search consistent with Core Data.
 *
 * @param {string}                           beverageType   - 'beer' | 'wine' | 'spirits'
 * @param {{ upc: string, label: string }[]} unitUpcOptions - Enriched UPC options from Core Data.
 * @param {object[]}                         products       - Current beverage grid rows.
 */
export const useBeverageGridColumns = (
  beverageType,
  unitUpcOptions = [],
  products = []
) => {
  const upcByUniqueId = useMemo(() => {
    const map = new Map();
    for (const product of products) {
      if (product.uniqueId) {
        map.set(product.uniqueId, product.unitUpc ?? null);
      }
    }
    return map;
  }, [products]);

  const columnDefs = useMemo(
    () => [
      createUpcColumn(unitUpcOptions, upcByUniqueId),
      ...(STATIC_COLUMN_MAP[beverageType] ?? []),
      // Hidden columns — not visible but scanned by quickFilterText
      ...HIDDEN_FILTER_COLUMNS,
    ],
    [beverageType, unitUpcOptions, upcByUniqueId]
  );

  return { columnDefs };
};

export default useBeverageGridColumns;
